import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { FileSpreadsheet, FileText, Download } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Spinner } from '@/shared/components/ui/Spinner';
import { getApiErrorMessage } from '@/shared/api/axios';
import { useReport, downloadReport, useBiometricEmployees } from '../hooks/useBiometricAdmin';
import { REPORT_OPTIONS } from '../biometricTypes';

const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

export function BiometricReports() {
  const { data: employees } = useBiometricEmployees();
  const [name, setName] = useState('daily');
  const [date, setDate] = useState(today);
  const [month, setMonth] = useState(() => today().slice(0, 7));
  const [from, setFrom] = useState(() => `${today().slice(0, 7)}-01`);
  const [to, setTo] = useState(today);
  const [department, setDepartment] = useState('');
  const [downloading, setDownloading] = useState(false);

  const option = REPORT_OPTIONS.find(o => o.value === name)!;

  const params = useMemo(() => {
    const base: Record<string, string> = {};
    if (department) base.department = department;
    if (option.param === 'date') base.date = date;
    else if (option.param === 'month') base.month = month;
    else {
      base.from = from;
      base.to = to;
    }
    return base;
  }, [option.param, date, month, from, to, department]);

  const { data: report, isLoading, error } = useReport(name, params);

  const departments = [...new Set((employees ?? []).map(e => e.department).filter(Boolean))] as string[];

  const download = async (format: 'csv' | 'xlsx' | 'pdf') => {
    setDownloading(true);
    try {
      await downloadReport(name, params, format);
      toast.success(`${format.toUpperCase()} downloaded.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card className="space-y-4 p-4">
      <div>
        <h2 className="text-lg font-semibold text-on-surface">Reports</h2>
        <p className="text-sm text-on-surface-variant">
          Built from the computed attendance rows. Recompute a period first if you have just changed a
          shift, roster or holiday.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-on-surface-variant">Report</label>
          <select
            className="h-10 rounded-md border border-outline-variant bg-surface px-2 text-sm text-on-surface"
            value={name}
            onChange={e => setName(e.target.value)}
          >
            {REPORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {option.param === 'date' && (
          <LabelledInput label="Date" type="date" value={date} onChange={setDate} />
        )}
        {option.param === 'month' && (
          <LabelledInput label="Month" type="month" value={month} onChange={setMonth} />
        )}
        {option.param === 'range' && (
          <>
            <LabelledInput label="From" type="date" value={from} onChange={setFrom} />
            <LabelledInput label="To" type="date" value={to} onChange={setTo} />
          </>
        )}

        <div>
          <label className="mb-1 block text-xs font-medium text-on-surface-variant">Department</label>
          <select
            className="h-10 rounded-md border border-outline-variant bg-surface px-2 text-sm text-on-surface"
            value={department}
            onChange={e => setDepartment(e.target.value)}
          >
            <option value="">All</option>
            {departments.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>

        <div className="ml-auto flex gap-2">
          <Button variant="secondary" onClick={() => download('csv')} disabled={downloading}>
            <Download className="mr-1 h-4 w-4" />
            CSV
          </Button>
          <Button variant="secondary" onClick={() => download('xlsx')} disabled={downloading}>
            <FileSpreadsheet className="mr-1 h-4 w-4" />
            Excel
          </Button>
          <Button variant="secondary" onClick={() => download('pdf')} disabled={downloading}>
            <FileText className="mr-1 h-4 w-4" />
            PDF
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-12"><Spinner /></div>
      ) : error ? (
        <p className="rounded bg-danger/10 p-3 text-sm text-danger-text">{getApiErrorMessage(error)}</p>
      ) : !report ? null : (
        <>
          <h3 className="font-medium text-on-surface">{report.title}</h3>
          {report.rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-on-surface-variant">
              No rows for this period.
            </p>
          ) : (
            <div className="max-h-[520px] overflow-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-surface">
                  <tr className="border-b border-outline-variant text-left uppercase tracking-wide text-on-surface-variant">
                    {report.columns.map(col => (
                      <th key={col} className="whitespace-nowrap px-2 py-1.5">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((row, i) => (
                    <tr key={i} className="border-b border-outline-variant/40 hover:bg-surface-variant/30">
                      {row.map((cell, j) => (
                        <td key={j} className="whitespace-nowrap px-2 py-1 text-on-surface">
                          {String(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-on-surface-variant">{report.rows.length} row(s).</p>
        </>
      )}
    </Card>
  );
}

function LabelledInput({
  label,
  type,
  value,
  onChange,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-on-surface-variant">{label}</label>
      <Input type={type} className="w-40" value={value} onChange={e => onChange(e.target.value)} />
    </div>
  );
}
